"use client";

import { useAuth } from "@/features/auth/context/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/lib/supabase";
import { useCallback, useEffect, useState } from "react";
import { FacilityModal } from "../components/FacilityModal";
import { FacilityTable } from "../components/FacilityTable";
import { Facility } from "../types";

export default function SettingsFacilities() {
  const { profile } = useAuth();
  const { toast } = useToast();
  const [facilities, setFacilities] = useState<Facility[]>([]);
  const [isFacilityModalOpen, setIsFacilityModalOpen] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState<
    Facility | undefined
  >();
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const fetchFacilities = useCallback(
    async (companyId: string) => {
      try {
        const { data: facilitiesData, error: facilitiesError } = await supabase
          .from("facilities")
          .select("*")
          .eq("company_id", companyId);

        if (facilitiesError) throw facilitiesError;

        setFacilities(facilitiesData || []);
      } catch (error) {
        console.error("Error fetching facilities:", error);
        toast({
          title: "Fehler",
          description: "Fehler beim Laden der Einrichtungen",
          variant: "destructive"
        });
      }
    },
    [toast]
  );

  const fetchCompanyId = useCallback(async () => {
    try {
      const { data: profileData, error: profileError } = await supabase
        .from("employer_profiles")
        .select("company_id")
        .eq("id", profile.id)
        .single();

      if (profileError) throw profileError;
      setCompanyId(profileData?.company_id);
      if (profileData?.company_id) {
        await fetchFacilities(profileData.company_id);
      }
    } catch (error) {
      console.error("Error fetching company ID:", error);
      toast({
        title: "Fehler",
        description: "Fehler beim Laden der Unternehmensdaten",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  }, [toast, fetchFacilities, profile.id]);

  useEffect(() => {
    if (!profile.anonymous) {
      fetchCompanyId();
    }
  }, [profile, fetchCompanyId]);

  const handleFacilitySave = async (
    facilityData: Omit<
      Facility,
      | "id"
      | "company_id"
      | "created_at"
      | "updated_at"
      | "job_profiles"
      | "subsidiary_id"
    >
  ) => {
    if (!companyId) {
      toast({
        title: "Fehler",
        description: "Keine Unternehmensdaten gefunden",
        variant: "destructive"
      });
      return;
    }

    setIsSaving(true);
    try {
      if (selectedFacility) {
        console.log(facilityData);
        // Update existing facility
        const { error: facilityError } = await supabase
          .from("facilities")
          .update({
            location: facilityData.location,
            facility_type: facilityData.facility_type,
            working_hours: facilityData.working_hours,
            shift_models: facilityData.shift_models,
            address: facilityData.address,
            street: facilityData.street,
            street_number: facilityData.street_number,
            postal_code: facilityData.postal_code,
            city: facilityData.city,
            state: facilityData.state,
            country: facilityData.country,
            updated_at: new Date().toISOString()
          })
          .eq("id", selectedFacility.id);

        if (facilityError) throw facilityError;

        toast({
          title: "Erfolg",
          description: "Einrichtung erfolgreich aktualisiert"
        });
      } else {
        // Create new facility
        console.log("create:", facilityData);
        const { error: facilityError } = await supabase
          .from("facilities")
          .insert({
            location: facilityData.location,
            facility_type: facilityData.facility_type,
            working_hours: facilityData.working_hours,
            shift_models: facilityData.shift_models,
            address: facilityData.address,
            street: facilityData.street,
            street_number: facilityData.street_number,
            postal_code: facilityData.postal_code,
            city: facilityData.city,
            state: facilityData.state,
            country: facilityData.country,
            company_id: companyId
          })
          .select()
          .single();

        if (facilityError) throw facilityError;

        toast({
          title: "Erfolg",
          description: "Einrichtung erfolgreich erstellt"
        });
      }

      await fetchFacilities(companyId);
      setIsFacilityModalOpen(false);
      setSelectedFacility(undefined);
    } catch (error) {
      console.error("Error saving facility:", error);
      toast({
        title: "Fehler",
        description: "Fehler beim Speichern der Einrichtung",
        variant: "destructive"
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handleFacilityDelete = async (facility: Facility) => {
    try {
      // Delete the facility
      const { error: deleteFacilityError } = await supabase
        .from("facilities")
        .delete()
        .eq("id", facility.id);

      if (deleteFacilityError) throw deleteFacilityError;

      toast({
        title: "Erfolg",
        description: "Einrichtung erfolgreich gelöscht"
      });
      if (companyId) {
        await fetchFacilities(companyId);
      }
    } catch (error) {
      console.error("Error deleting facility:", error);
      toast({
        title: "Fehler",
        description: "Fehler beim Löschen der Einrichtung",
        variant: "destructive"
      });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="text-gray-500">Lade Daten...</div>
      </div>
    );
  }

  if (!companyId) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="text-gray-500">Keine Unternehmensdaten gefunden</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-lg font-semibold">Einrichtungen</h2>
        <p className="text-sm text-gray-500">
          Verwalte die Einrichtungen deines Unternehmens
        </p>
      </div>
      <div>
        <FacilityTable
          facilities={facilities}
          onEdit={(facility) => {
            setSelectedFacility(facility);
            setIsFacilityModalOpen(true);
          }}
          onDelete={handleFacilityDelete}
          onAdd={() => {
            setSelectedFacility(undefined);
            setIsFacilityModalOpen(true);
          }}
        />
      </div>

      <FacilityModal
        open={isFacilityModalOpen}
        onOpenChange={setIsFacilityModalOpen}
        onSave={handleFacilitySave}
        facility={selectedFacility}
        title={selectedFacility ? "Einrichtung bearbeiten" : "Neue Einrichtung"}
        isSaving={isSaving}
      />
    </div>
  );
}
